'use client'

import { useEffect, useState } from 'react'

const SCRIPT_URL = 'https://analytics.underthegan.art/script.js'
const WEBSITE_ID = '0e543d28-0993-43a1-afb3-543e5d4123bd'
const HOSTS = new Set(['https://orkhan.art', 'https://orkhan.design'])
const CONSENT_KEY = 'site-recording-consent'

/** What the artist set in Control › Analytics › Settings. */
export type AnalyticsSettings = {
  /** Count visits with Umami. */
  count: boolean
  /** Pages that are neither counted nor recorded: the path itself and everything under it. */
  excluded: string[]
  /** Microsoft Clarity project id; null leaves heatmaps and recordings off. */
  clarityId: string | null
}
export const defaultAnalytics: AnalyticsSettings = { count: true, excluded: [], clarityId: null }

export function isExcluded(pathname: string, excluded: string[]): boolean {
  return excluded.some(path => path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`))
}

const live = () => process.env.NODE_ENV === 'production' && HOSTS.has(window.location.origin)

/** Cookieless Umami. Production hosts only, so preview and local builds do not write into the live bucket. */
export function mountAnalytics(settings: AnalyticsSettings = defaultAnalytics): void {
  if (!live() || !settings.count) return
  if (document.querySelector(`script[data-website-id="${WEBSITE_ID}"]`)) return

  // Umami asks this before every send; returning false drops the hit.
  ;(window as unknown as Record<string, unknown>).__skipExcluded = (_type: string, payload: { url?: string }) => {
    const path = (payload.url || window.location.pathname).split(/[?#]/)[0]
    return isExcluded(path, settings.excluded) ? false : payload
  }
  const script = document.createElement('script')
  script.src = SCRIPT_URL
  script.defer = true
  script.dataset.websiteId = WEBSITE_ID
  script.dataset.domains = 'orkhan.art,orkhan.design'
  script.dataset.beforeSend = '__skipExcluded'
  document.head.appendChild(script)
}

/** Microsoft Clarity: heatmaps and recordings. It sets cookies, so it loads only after the visitor allows it. */
function mountClarity(id: string): void {
  if (document.querySelector('script[data-clarity]')) return
  const w = window as unknown as { clarity?: ((...args: unknown[]) => void) & { q?: unknown[] } }
  w.clarity = w.clarity || function (...args: unknown[]) { (w.clarity!.q = w.clarity!.q || []).push(args) }
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.clarity.ms/tag/${id}`
  script.dataset.clarity = id
  document.head.appendChild(script)
}

const readConsent = (): string | null => { try { return window.localStorage.getItem(CONSENT_KEY) } catch { return null } }

export function Umami({ settings = defaultAnalytics }: { settings?: AnalyticsSettings }) {
  const [ask, setAsk] = useState(false)

  useEffect(() => {
    mountAnalytics(settings)
    if (!settings.clarityId || !live() || isExcluded(window.location.pathname, settings.excluded)) return undefined
    const consent = readConsent()
    if (consent === 'yes') mountClarity(settings.clarityId)
    if (consent !== null) return undefined
    // Asked once the page has settled, so the banner does not compete with the first paint.
    const timer = window.setTimeout(() => setAsk(true), 1500)
    return () => window.clearTimeout(timer)
  }, [settings])

  function answer(allow: boolean) {
    try { window.localStorage.setItem(CONSENT_KEY, allow ? 'yes' : 'no') } catch { /* asked again next visit */ }
    setAsk(false)
    if (allow && settings.clarityId) mountClarity(settings.clarityId)
  }

  if (!ask) return null
  return (
    <div role="dialog" aria-label="Recording this visit" className="fixed bottom-3 left-3 z-[1000] flex max-w-[320px] flex-col gap-2 rounded-md border border-white/15 bg-black p-3 font-mono text-[10px] uppercase leading-relaxed tracking-wide text-white shadow-lg">
      <p className="m-0 normal-case text-white/80">May this visit be recorded to see how the pages are used? It sets a cookie. Visits are counted without one either way.</p>
      <div className="flex gap-2">
        <button type="button" onClick={() => answer(true)} className="rounded bg-white px-2 py-1 text-black">Allow</button>
        <button type="button" onClick={() => answer(false)} className="rounded border border-white/30 px-2 py-1 text-white">No thanks</button>
      </div>
    </div>
  )
}
